import clsx from 'clsx';

import { List } from './List';
import {
    ListItem,
    ListItemBody,
    ListItemControls,
    ListItemDescription,
    ListItemDetail,
    ListItemIndicator,
    type ListItemProps,
} from './ListItem';

type ListItemPart = 'indicator' | 'detail' | 'controls';

/**
 * A skeleton list component with a pulsating animation. To be used where more appropriate then the
 * common LoadingIndicator.
 *
 * @param props.items The number of skeleton items to render.
 * @param props.excludeParts An array of parts to exclude from the skeleton items.
 */
export function ListSkeleton(props: {
    items?: number;
    compact?: boolean;
    excludeParts?: ListItemPart[];
    className?: string;
}) {
    const { items = 3, compact = false, excludeParts = [], className } = props;

    return (
        <div role="status" aria-busy className={clsx('motion-safe:animate-pulse', className)}>
            <List
                items={[...Array(items).keys()]}
                renderItem={() => (
                    <ListItemSkeleton compact={compact} excludeParts={excludeParts} />
                )}
            />
        </div>
    );
}

/**
 * Skeleton for a single list item.
 */
export function ListItemSkeleton(
    props: { excludeParts?: ListItemPart[] } & Pick<ListItemProps, 'compact'>
) {
    const { excludeParts = [], compact } = props;

    return (
        <ListItem compact={compact}>
            {excludeParts.includes('indicator') ? null : (
                <ListItemIndicator>
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary" />
                </ListItemIndicator>
            )}

            <ListItemBody>
                <ListItemDescription>
                    <div className="mb-2 h-3 w-28 rounded-full bg-secondary" />
                </ListItemDescription>
                {excludeParts.includes('detail') ? null : (
                    <ListItemDetail>
                        <div className="h-2 w-14 rounded-full bg-secondary" />
                    </ListItemDetail>
                )}
            </ListItemBody>

            {excludeParts.includes('controls') ? null : (
                <ListItemControls when="always">
                    <div className="h-2 w-10 rounded-full bg-secondary" />
                </ListItemControls>
            )}
        </ListItem>
    );
}
